<template lang="pug">
.selection
    .empty(v-if="!selectedField") {{ $t(`${baseTranslationPath}.select_field_hint`) }}
    template(v-else)
        .sel-header
            .sel-heading {{ $t(`${baseTranslationPath}.selected_field`) }}
            .sel-field
                .sel-index(v-if="fieldIndex") {{ fieldIndex }}
                font-awesome-icon.sel-icon(:icon="fieldIcon")
                .sel-field-info
                    .sel-field-name {{ selectedField.label || selectedField.field_name || selectedField.field_key }}
                    .sel-field-sub {{ $t(`${baseTranslationPath}.detected_as`) }} {{ selectedField.field_type }}
                aiq-button(
                    size="small"
                    type="primary"
                    :loading="saving"
                    :disabled="!dirty"
                    @click="save"
                ) {{ $t(`${baseTranslationPath}.save_mapping`) }}
        .field-selection
            .add-entity
                aiq-select(
                    filterable
                    size="small"
                    :model-value="null"
                    :placeholder="$t(`${baseTranslationPath}.add_entity_ph`)"
                    no-data-text="None available"
                    @change="addEntity"
                )
                    aiq-option(
                        v-for="entity in entityOptions"
                        :key="entity"
                        :value="entity"
                        :label="entity"
                    )
            .mapping-hint {{ $t(`${baseTranslationPath}.mapping_order_hint`) }}
            .empty(v-if="!mappings.length") {{ $t(`${baseTranslationPath}.no_mappings`) }}
            draggable.map-list(
                v-else
                :list="mappings"
                item-key="id"
                handle=".drag-handle"
            )
                template(#item="{ element, index }")
                    .map-item
                        font-awesome-icon.drag-handle(:icon="['fas', 'grip-vertical']")
                        .order-index {{ index + 1 }}
                        .field-name {{ element.label }}
                        fa-icon.remove-icon(icon="fas fa-xmark" @click="remove(element)")
        .suggested(v-if="suggestedKeys.length")
            .sug-title {{ $t(`${baseTranslationPath}.suggested`) }}
            .sug-item(v-for="(key, index) in suggestedKeys" :key="`sug-${index}`")
                .order-index.muted {{ index + 1 }}
                .field-name {{ key }}
</template>
<script>
import { createNamespacedHelpers } from 'vuex';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import { fieldIconTypes } from './fieldUtil';

const { mapGetters } = createNamespacedHelpers('entities');

export default {
  name: 'Selection',
  props: {
    // form_fields row (has mapped_keys / suggested_keys), or null.
    selectedField: {
      type: Object,
      default: null,
    },
    formId: {
      type: [String, Number],
      default: null,
    },
    // 1-based index of the selected field within the form's field list.
    fieldIndex: {
      type: Number,
      default: null,
    },
  },
  emits: ['updated'],
  data() {
    return {
      baseTranslationPath: 'iq_tools_tab.forms_tab.manage',
      mappings: [],
      original: [],
      saving: false,
    };
  },
  computed: {
    ...mapGetters(['selectionList']),
    fieldIcon() {
      return fieldIconTypes(get(this.selectedField, 'field_type')).icon;
    },
    // Entity source paths available to add, minus the ones already mapped.
    entityOptions() {
      const mapped = new Set(this.mappings.map(m => m.label));
      return this.selectionList
        .map(entity => entity.name)
        .filter(name => name && !mapped.has(name));
    },
    // Matcher suggestions, shown read-only for reference.
    suggestedKeys() {
      const suggested = get(this.selectedField, 'suggested_keys', []) || [];
      return suggested.map(item => (typeof item === 'string' ? item : get(item, 'source_path', String(item))));
    },
    // the current ordered list of source paths (what we persist as mapped_keys).
    orderedKeys() {
      return this.mappings.map(m => m.label);
    },
    dirty() {
      return !isEqual(this.orderedKeys, this.original);
    },
  },
  watch: {
    selectedField: {
      immediate: true,
      deep: true,
      handler(next) {
        this.load(next);
      },
    },
  },
  mounted() {
    // Populate the entity selector; safe to re-dispatch (cached in the store).
    this.$store.dispatch('entities/getEntitiesList');
  },
  methods: {
    load(field) {
      // Persisted mappings are authoritative; suggestions stay read-only.
      const mapped = get(field, 'mapped_keys', []) || [];
      const keys = mapped.map(item => (typeof item === 'string' ? item : get(item, 'source_path', String(item))));
      this.mappings = keys.map((label, id) => ({ label, id }));
      this.original = [...keys];
    },
    addEntity(name) {
      if (!name || this.mappings.some(m => m.label === name)) {
        return;
      }
      const id = this.mappings.reduce((max, m) => Math.max(max, m.id), -1) + 1;
      this.mappings = [...this.mappings, { label: name, id }];
    },
    remove(item) {
      this.mappings = this.mappings.filter(f => f.id !== item.id);
    },
    async save() {
      if (!this.selectedField || !this.formId) {
        return;
      }
      this.saving = true;
      try {
        await this.$store.dispatch('agentForms/updateFormField', {
          formId: this.formId,
          fieldId: this.selectedField.id,
          mapped_keys: this.orderedKeys,
        });
        this.original = [...this.orderedKeys];
        this.$aiq.notify.success(this.$t(`${this.baseTranslationPath}.mapping_saved`));
        this.$emit('updated');
      } catch (_) {
        this.$aiq.notify.error(this.$t(`${this.baseTranslationPath}.mapping_save_error`));
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
<style lang="scss" scoped>
@import 'src/styles/aiq-variables.scss';

.selection {
  padding: 14px;
  height: 100%;
  overflow-y: auto;
}

.sel-header {
  margin-bottom: 12px;
  padding-bottom: 10px;
  border-bottom: 1px solid $aiq-tab-content-border;
}

.sel-heading {
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 10px;
}

.sel-field {
  display: flex;
  align-items: center;
  gap: 10px;
}

.sel-icon {
  color: $aiq-input-border-hover;
  font-size: 16px;
}

.sel-index {
  width: 22px;
  height: 22px;
  border-radius: 4px;
  background-color: $aiq-color-black;
  color: $aiq-color-white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  flex-shrink: 0;
}

.sel-field-info {
  min-width: 0;
}

.sel-field-name {
  font-size: 14px;
  font-weight: 600;
}

.sel-field-sub {
  font-size: 12px;
  color: $aiq-color-text-secondary;
  margin-top: 2px;
}

.sel-field :deep(.el-button) {
  margin-left: auto;
}

.add-entity {
  margin-bottom: 12px;

  :deep(.el-select) {
    width: 100%;
  }
}

.mapping-hint {
  font-size: 12px;
  color: $aiq-color-text-secondary;
  margin-bottom: 10px;
}

.empty {
  color: $aiq-color-text-secondary;
  font-size: 13px;
  padding: 16px;
  text-align: center;
}

.order-index {
  width: 22px;
  height: 22px;
  border-radius: 4px;
  background-color: $aiq-color-black;
  color: $aiq-color-white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  flex-shrink: 0;

  &.muted {
    background-color: $aiq-input-border-hover;
  }
}

.map-item {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  height: 40px;
  gap: 10px;
  background-color: $aiq-tab-content-background;
  border: 1px solid $aiq-tab-content-background;
  padding: 0 10px;
  border-radius: 5px;
}

.field-name {
  font-size: 14px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.drag-handle {
  cursor: grab;
  color: $aiq-input-border-hover;
}

.remove-icon {
  cursor: pointer;
  margin-left: auto;
  color: $aiq-input-border-hover;

  &:hover {
    color: $aiq-color-error;
  }
}

.suggested {
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid $aiq-tab-content-border;
}

.sug-title {
  font-size: 13px;
  font-weight: 600;
  color: $aiq-color-text-secondary;
  margin-bottom: 8px;
}

.sug-item {
  display: flex;
  align-items: center;
  gap: 10px;
  height: 34px;
  padding: 0 10px;
  margin-bottom: 6px;
  border: 1px dashed $aiq-icon-primary;
  border-radius: 5px;
  color: $aiq-color-text-secondary;
}
</style>
